import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Tabs, Tab, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Chip, IconButton, Tooltip, Button, Dialog,
  DialogTitle, DialogContent, DialogActions, CircularProgress
} from '@mui/material';
import SmsIcon from '@mui/icons-material/Sms';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import LogoutIcon from '@mui/icons-material/Logout';
import { GetQueue, TransitionVisit, SendSmsRecall, ReorderQueue, Subscribe, Unsubscribe } from '../../../api';
import { socket } from '../../../api';
import dayjs from 'dayjs';

const STATE_LABELS = {
  checked_in: 'Checked In',
  acknowledged: 'Acknowledged',
  called_in: 'Called In',
  in_service: 'In Service',
  completed: 'Completed',
  cancelled: 'Cancelled',
  no_show: 'No Show',
};

const STATE_COLORS = {
  checked_in: 'warning',
  acknowledged: 'info',
  called_in: 'secondary',
  in_service: 'primary',
  completed: 'success',
  cancelled: 'default',
  no_show: 'default',
};

const NEXT_STATE = {
  checked_in: 'acknowledged',
  acknowledged: 'called_in',
  called_in: 'in_service',
  in_service: 'completed',
};

const fmtTime = ts => ts ? dayjs(ts).format('h:mm A') : '—';
const elapsed = ts => ts ? `${dayjs().diff(dayjs(ts), 'minute')}m` : '—';

const SmsConfirmDialog = ({ open, visit, onConfirm, onClose }) => (
  <Dialog open={open} onClose={onClose}>
    <DialogTitle>Send SMS Recall</DialogTitle>
    <DialogContent>
      <Typography>Send a text message to {visit?.ownerName} at {visit?.phone} asking them to come inside?</Typography>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Cancel</Button>
      <Button variant="contained" onClick={onConfirm}>Send</Button>
    </DialogActions>
  </Dialog>
);

const StandardDisplay = ({ visits, onTransition, onSms, onReorder, loading }) => (
  <TableContainer component={Paper}>
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>#</TableCell>
          <TableCell>Owner</TableCell>
          <TableCell>Pet</TableCell>
          <TableCell>Space</TableCell>
          <TableCell>Phone</TableCell>
          <TableCell>Reason</TableCell>
          <TableCell>Time In</TableCell>
          <TableCell>Wait</TableCell>
          <TableCell>Status</TableCell>
          <TableCell>Actions</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {loading && (
          <TableRow><TableCell colSpan={10} align="center"><CircularProgress size={24} /></TableCell></TableRow>
        )}
        {!loading && !visits.length && (
          <TableRow><TableCell colSpan={10} align="center"><Typography variant="body2" color="text.secondary">No visits in queue</Typography></TableCell></TableRow>
        )}
        {visits.map((v, i) => (
          <TableRow key={v.id} hover>
            <TableCell>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <IconButton size="small" disabled={i === 0} onClick={() => onReorder(v.id, 'up')}><ArrowUpwardIcon fontSize="inherit" /></IconButton>
                <IconButton size="small" disabled={i === visits.length - 1} onClick={() => onReorder(v.id, 'down')}><ArrowDownwardIcon fontSize="inherit" /></IconButton>
              </Box>
            </TableCell>
            <TableCell>{v.ownerName}</TableCell>
            <TableCell>{v.petName}</TableCell>
            <TableCell>{v.parkingSpace || '—'}</TableCell>
            <TableCell>{v.phone || '—'}</TableCell>
            <TableCell>{[v.reason?.label, v.subreason?.label].filter(Boolean).join(' / ') || '—'}</TableCell>
            <TableCell>{fmtTime(v.createdAt)}</TableCell>
            <TableCell>{elapsed(v.createdAt)}</TableCell>
            <TableCell><Chip label={STATE_LABELS[v.currentState] || v.currentState} color={STATE_COLORS[v.currentState] || 'default'} size="small" /></TableCell>
            <TableCell>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                {NEXT_STATE[v.currentState] && (
                  <Tooltip title={`Mark as ${STATE_LABELS[NEXT_STATE[v.currentState]]}`}>
                    <IconButton size="small" color="primary" onClick={() => onTransition(v.id, NEXT_STATE[v.currentState])}>
                      <CheckCircleOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {v.currentState !== 'completed' && v.currentState !== 'cancelled' && v.phone && (
                  <Tooltip title="Send SMS recall">
                    <IconButton size="small" onClick={() => onSms(v)}>
                      <SmsIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {v.currentState === 'in_service' && (
                  <Tooltip title="Check out">
                    <IconButton size="small" color="success" onClick={() => onTransition(v.id, 'completed')}>
                      <LogoutIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

const StatusDisplay = ({ visits }) => (
  <TableContainer component={Paper}>
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Owner</TableCell>
          <TableCell>Pet</TableCell>
          <TableCell>Time In</TableCell>
          <TableCell>Status</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {visits.map(v => (
          <TableRow key={v.id} hover>
            <TableCell>{v.ownerName}</TableCell>
            <TableCell>{v.petName}</TableCell>
            <TableCell>{fmtTime(v.createdAt)}</TableCell>
            <TableCell><Chip label={STATE_LABELS[v.currentState] || v.currentState} color={STATE_COLORS[v.currentState] || 'default'} size="small" /></TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

const SidebarDisplay = ({ visits }) => (
  <Box sx={{ maxWidth: 320 }}>
    {visits.map(v => (
      <Paper key={v.id} sx={{ p: 1.5, mb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="body2" fontWeight={500}>{v.ownerName}</Typography>
          <Typography variant="caption" color="text.secondary">{v.petName} · {fmtTime(v.createdAt)}</Typography>
        </Box>
        <Chip label={STATE_LABELS[v.currentState] || v.currentState} color={STATE_COLORS[v.currentState] || 'default'} size="small" />
      </Paper>
    ))}
    {!visits.length && <Typography variant="body2" color="text.secondary">Queue is empty</Typography>}
  </Box>
);

const Queue = ({ user, access, socketStatus }) => {
  const [tab, setTab] = useState(0);
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [smsTarget, setSmsTarget] = useState(null);

  const orgId = access?.org?.id;

  const load = useCallback(() => {
    if (!orgId) return;
    GetQueue(orgId)
      .then(data => setVisits(data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [orgId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!socket || !orgId) return;
    const sub = { coll: 'lobbyVisits', type: 'insert', id: orgId };
    Subscribe(sub, load);
    const subUpdate = { coll: 'lobbyVisits', type: 'update', id: orgId };
    Subscribe(subUpdate, load);
    return () => { Unsubscribe(sub); Unsubscribe(subUpdate); };
  }, [orgId, load]);

  const handleTransition = async (visitId, newState) => {
    await TransitionVisit(visitId, orgId, { state: newState });
    load();
  };

  const handleReorder = async (visitId, direction) => {
    await ReorderQueue(orgId, { visitId, direction });
    load();
  };

  const handleSmsConfirm = async () => {
    if (!smsTarget) return;
    await SendSmsRecall(smsTarget.id, orgId);
    setSmsTarget(null);
    load();
  };

  const activeVisits = visits.filter(v => !['completed', 'cancelled', 'no_show'].includes(v.currentState));

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">Queue</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: socketStatus ? 'success.main' : 'error.main' }} />
          <Typography variant="caption" color="text.secondary">{socketStatus ? 'Live' : 'Reconnecting…'}</Typography>
        </Box>
      </Box>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Standard" />
        <Tab label="Status" />
        <Tab label="Sidebar" />
      </Tabs>
      {tab === 0 && <StandardDisplay visits={activeVisits} onTransition={handleTransition} onSms={setSmsTarget} onReorder={handleReorder} loading={loading} />}
      {tab === 1 && <StatusDisplay visits={activeVisits} />}
      {tab === 2 && <SidebarDisplay visits={activeVisits} />}
      <SmsConfirmDialog open={!!smsTarget} visit={smsTarget} onConfirm={handleSmsConfirm} onClose={() => setSmsTarget(null)} />
    </Box>
  );
};

export default Queue;
